import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import AppNav from '../AppNav/AppNav';
import MatterTitleField from '../MatterTitleField/MatterTitleField';
import { formatTokenMessage, sharedMessages } from '../../utils/uiMessages';

const initialForm = {
  title: '',
  client_name: '',
  application_number: '',
  matter_reference_code: '',
  jurisdiction: '',
  matter_status: 'open',
  responsible_attorney: '',
  notes: '',
};

const messages = defineMessages({
  applicationNumberHelp: {
    id: 'Example: 特願2020-123456',
    defaultMessage: 'Example: 特願2020-123456',
  },
  clientName: {
    id: 'Client name',
    defaultMessage: 'Client name',
  },
  createMatterNote: {
    id: 'Create the matter first, then register jobs inside it.',
    defaultMessage: 'Create the matter first, then register jobs inside it.',
  },
  matterCreatedAt: {
    id: 'Matter created at {url}',
    defaultMessage: 'Matter created at {url}',
  },
  requestFailedWithStatus: {
    id: 'Request failed with status {status}',
    defaultMessage: 'Request failed with status {status}',
  },
});

export default function MatterForm() {
  const intl = useIntl();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [createdUrl, setCreatedUrl] = useState('');

  async function onSubmit(event) {
    event.preventDefault();
    setLoading(true);
    setError('');
    setCreatedUrl('');

    try {
      const normalizedMatterReferenceCode = normalizeMatterReference(
        form.matter_reference_code,
      );
      const response = await fetch('/++api++/', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          '@type': 'Matter',
          ...form,
          matter_reference_code: normalizedMatterReferenceCode,
          matter_reference: normalizedMatterReferenceCode,
        }),
      });

      if (!response.ok) {
        throw new Error(await extractError(response, intl));
      }

      const payload = await response.json();
      setCreatedUrl(payload['@id'] || '');
      setForm(initialForm);
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="patent-entry">
      <section className="patent-entry__shell">
        <AppNav currentPath="/matters" />
        <header className="patent-entry__hero">
          <p className="patent-entry__eyebrow">
            {intl.formatMessage(sharedMessages.patentManager)}
          </p>
          <h1>{intl.formatMessage(sharedMessages.registerMatter)}</h1>
          <p className="patent-entry__note">
            {intl.formatMessage(messages.createMatterNote)}
          </p>
          <div className="patent-entry__actions patent-entry__actions--row">
            <Link className="patent-entry__ghost" to="/overview">
              {intl.formatMessage(sharedMessages.backToOverview)}
            </Link>
          </div>
        </header>

        <form className="patent-entry__form" onSubmit={onSubmit}>
          <TextField
            label={intl.formatMessage(sharedMessages.matterReferenceCode)}
            value={form.matter_reference_code}
            onChange={(value) =>
              setForm((current) => ({ ...current, matter_reference_code: value }))
            }
          />
          <MatterTitleField
            value={form.title}
            onChange={(value) => setForm((current) => ({ ...current, title: value }))}
            required
          />
          <TextField
            label={intl.formatMessage(messages.clientName)}
            value={form.client_name}
            onChange={(value) =>
              setForm((current) => ({ ...current, client_name: value }))
            }
          />
          <TextField
            label={intl.formatMessage(sharedMessages.applicationNumber)}
            value={form.application_number}
            onChange={(value) =>
              setForm((current) => ({ ...current, application_number: value }))
            }
            helpText={intl.formatMessage(messages.applicationNumberHelp)}
          />
          <TextField
            label={intl.formatMessage(sharedMessages.jurisdiction)}
            value={form.jurisdiction}
            onChange={(value) =>
              setForm((current) => ({ ...current, jurisdiction: value }))
            }
          />
          <SelectField
            label={intl.formatMessage(sharedMessages.matterStatus)}
            value={form.matter_status}
            onChange={(value) =>
              setForm((current) => ({ ...current, matter_status: value }))
            }
            options={[
              'open',
              'awaiting_instruction',
              'active_prosecution',
              'allowed',
              'abandoned',
              'granted',
              'closed',
            ]}
          />
          <TextField
            label={intl.formatMessage(sharedMessages.responsibleAttorney)}
            value={form.responsible_attorney}
            onChange={(value) =>
              setForm((current) => ({ ...current, responsible_attorney: value }))
            }
          />
          <TextAreaField
            label={intl.formatMessage(sharedMessages.notes)}
            value={form.notes}
            onChange={(value) => setForm((current) => ({ ...current, notes: value }))}
          />

          {error ? <p className="patent-entry__error">{error}</p> : null}
          {createdUrl ? (
            <p className="patent-entry__success">
              {intl.formatMessage(messages.matterCreatedAt, {
                url: createdUrl,
              })}
            </p>
          ) : null}

          <div className="patent-entry__actions">
            <button className="patent-entry__submit" type="submit" disabled={loading}>
              {loading
                ? intl.formatMessage(sharedMessages.creating)
                : intl.formatMessage(sharedMessages.createMatter)}
            </button>
          </div>
        </form>
      </section>
    </main>
  );
}

function TextField({ label, value, onChange, required = false, helpText = '' }) {
  return (
    <label className="patent-entry__field">
      <span className="patent-entry__label">{label}</span>
      <input
        className="patent-entry__input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
      />
      {helpText ? <span className="patent-entry__help">{helpText}</span> : null}
    </label>
  );
}

function TextAreaField({ label, value, onChange }) {
  return (
    <label className="patent-entry__field patent-entry__field--wide">
      <span className="patent-entry__label">{label}</span>
      <textarea
        className="patent-entry__textarea"
        rows="5"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  const intl = useIntl();

  return (
    <label className="patent-entry__field">
      <span className="patent-entry__label">{label}</span>
      <select
        className="patent-entry__input"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {formatTokenMessage(intl, option)}
          </option>
        ))}
      </select>
    </label>
  );
}

async function extractError(response, intl) {
  try {
    const payload = await response.json();
    return (
      payload.message ||
      payload.error?.message ||
      intl.formatMessage(messages.requestFailedWithStatus, {
        status: response.status,
      })
    );
  } catch {
    return intl.formatMessage(messages.requestFailedWithStatus, {
      status: response.status,
    });
  }
}

function normalizeMatterReference(value) {
  const trimmed = (value || '').trim();
  return trimmed || 'not assigned';
}
